import { useEffect, useState } from 'react';

interface ScrollSpyOptions {
  // CSS selector for target sections (default: 'section[id]')
  sectionSelector?: string;

  // Top viewport offset in pixels to mark section as active
  offset?: number;

  // Scroll event debounce time in ms
  debounceTime?: number;

  // Update URL hash with active section ID
  updateURL?: boolean;

  // Update URL hash on initial page load
  updateOnLoad?: boolean;
}

/**
 * Scroll spy hook that tracks active section and updates URL hash
 *
 * @param options - Configuration options
 * @returns Active section ID
 */
export function useScrollSpy({
  sectionSelector = 'section[id]',
  offset = 90, // Slightly higher than header height
  debounceTime = 100,
  updateURL = true,
  updateOnLoad = false,
}: ScrollSpyOptions = {}): string {
  const [activeSection, setActiveSection] = useState<string>('');

  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout>;
    let sections: Element[] = [];

    // Get sections on mount and resize
    const getSections = () => {
      sections = Array.from(document.querySelectorAll(sectionSelector));
    };

    // Determine which section is currently in view
    const findActiveSection = () => {
      // Clear debounce timer
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      // Debounce scroll event
      debounceTimer = setTimeout(() => {
        if (sections.length === 0) return;

        const scrollPosition = window.scrollY + offset;
        let currentSection = '';

        // Find last section that starts before current scroll position
        for (const section of sections) {
          const sectionTop = section.getBoundingClientRect().top + window.scrollY;
          const sectionId = section.id;

          if (sectionTop <= scrollPosition) {
            currentSection = sectionId;
          } else {
            break;
          }
        }

        // At page top with no section in view, use first section
        if (!currentSection && sections.length > 0 && scrollPosition <= offset) {
          currentSection = sections[0].id;
        }

        // Past all sections, use last section
        if (!currentSection && sections.length > 0) {
          currentSection = sections[sections.length - 1].id;
        }

        // Only update if the active section has changed
        if (currentSection !== activeSection) {
          setActiveSection(currentSection);

          // Update URL hash without page reload
          if (updateURL && currentSection) {
            const url = new URL(window.location.href);
            url.hash = currentSection;
            window.history.replaceState(null, '', url.toString());
          }
        }
      }, debounceTime);
    };

    // Initialize
    getSections();

    // Check for active section on load if enabled
    if (updateOnLoad) {
      findActiveSection();
    } else {
      // Use hash from URL if present
      const initialHash = window.location.hash.slice(1);
      if (initialHash) {
        setActiveSection(initialHash);
      }
    }

    // Register event listeners
    window.addEventListener('scroll', findActiveSection, { passive: true });
    window.addEventListener('resize', getSections, { passive: true });

    // Cleanup
    return () => {
      window.removeEventListener('scroll', findActiveSection);
      window.removeEventListener('resize', getSections);
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [sectionSelector, offset, debounceTime, updateURL, updateOnLoad, activeSection]);

  return activeSection;
}
